interface RawBreadcrumb {
    key?: string;
    params?: Record<string, string | number>;
    text?: string;
    path?: string;
    href?: string;
}

type BreadcrumbMeta =
    string | RawBreadcrumb | RawBreadcrumb[] | ((route: ReturnType<typeof useRoute>) => BreadcrumbMeta);

export default defineNuxtPlugin((nuxtApp) => {
    const route = useRoute();
    const localePath = useLocalePath();
    const crumbsState = useState("breadcrumbs", () => []);

    // Use i18n instance from nuxt app (avoid useI18n() setup restriction)
    const t = nuxtApp.$i18n.t.bind(nuxtApp.$i18n);

    const normalize = (entry: BreadcrumbMeta, r: typeof route): RawBreadcrumb[] => {
        if (typeof entry === "function") {
            return normalize(entry(r), r);
        }
        if (typeof entry === "string") {
            return [{ key: entry }];
        }
        if (Array.isArray(entry)) {
            return entry.flatMap((e) => normalize(e, r));
        }

        return [entry];
    };

    const buildFromMeta = () => {
        const built: { text: string; href?: string }[] = [];

        for (const record of route.matched) {
            const metaEntry = (record.meta as object)?.breadcrumb as BreadcrumbMeta | undefined;
            if (!metaEntry) {
                continue;
            }
            const rawItems = normalize(metaEntry, route);

            for (const raw of rawItems) {
                const text = raw.text ?? (raw.key ? t(raw.key, raw.params || {}) : "");
                if (!text) {
                    continue;
                }
                const href = raw.href ? raw.href : raw.path ? localePath(raw.path) : undefined;
                built.push({ text, href });
            }
        }

        crumbsState.value = built;
    };

    watch(() => route.fullPath, buildFromMeta, { immediate: true });
});
